import React from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { Formik, Form } from 'formik'
import * as y from 'yup'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ClipboardCheck, Copy, LoaderCircle } from 'lucide-react'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useRouter } from '../../../hooks'
import SubmittalForm, { SubmittalFormValues } from './SubmittalForm'
import { copyInitialValues, SubmittalCopySource } from './submittal-copy'

interface SubmittalCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  onCreated?: () => void
  /** "Copy to new": prefill every field from this submittal; the number stays blank. */
  source?: SubmittalCopySource
}

type FormValues = SubmittalFormValues

const INITIAL_VALUES: FormValues = {
  displayNumber: '',
  title: '',
  description: '',
  companyName: '',
  responsibleId: '',
  dueDate: null,
  specSectionId: '',
  categoryId: '',
  subcategoryId: '',
  disciplineId: '',
  packageId: '',
}

const SubmittalCreateDialog: React.FC<SubmittalCreateDialogProps> = ({
  open,
  onOpenChange,
  projectId,
  onCreated,
  source,
}) => {
  const { t } = useTranslation()
  const { history } = useRouter()

  const { data: lookupsData } = useQuery(Query.SUBMITTAL_LOOKUP_VALUES, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: membersData } = useQuery(Query.PROJECT_MEMBERS, {
    variables: { projectId },
    skip: !projectId,
  })
  const { data: suggestionsData } = useQuery(Query.SUBMITTAL_COMPANY_SUGGESTIONS, {
    variables: { projectId },
    skip: !projectId,
  })

  const [createSubmittal] = useMutation(Mutation.CREATE_SUBMITTAL)

  const lookups = lookupsData?.submittalLookupValues || []
  const members = (membersData?.project?.users ?? []).map((m: any) => m.user).filter(Boolean)
  const companySuggestions: string[] = suggestionsData?.submittalCompanySuggestions || []

  const validationSchema = y.object().shape({
    title: y.string().trim().required(t('Submittals_validation_title_required')),
  })

  const handleClose = () => onOpenChange(false)

  const handleSubmit = async (values: FormValues, { setSubmitting, setStatus }: any) => {
    setStatus(undefined)
    try {
      const result = await createSubmittal({
        variables: {
          input: {
            projectId,
            displayNumber: values.displayNumber?.trim() || null,
            title: values.title.trim(),
            description: values.description.trim() || null,
            companyName: values.companyName.trim() || null,
            responsibleId: values.responsibleId || null,
            dueDate: values.dueDate || null,
            specSectionId: values.specSectionId || null,
            categoryId: values.categoryId || null,
            subcategoryId: values.subcategoryId || null,
            disciplineId: values.disciplineId || null,
            packageId: values.packageId || null,
          },
        },
      })
      const created = result?.data?.createSubmittal
      toastr.success('', t('Submittals_created'))
      onCreated?.()
      onOpenChange(false)
      if (created?.id) {
        history.push(`/project/${projectId}/submittals/${created.id}`)
      }
    } catch (error: any) {
      setStatus({ submitError: error.message || t('Common_error_occurred') })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {source ? <Copy className="h-5 w-5 text-primary" /> : <ClipboardCheck className="h-5 w-5 text-primary" />}
            {source ? t('Submittals_copy_title', { number: source.displayNumber }) : t('Submittals_create_title')}
          </DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={source ? copyInitialValues(source) : INITIAL_VALUES}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="flex-1 overflow-y-auto min-h-0 p-1.5 -m-1.5">
              {/* p-1.5/-m-1.5 above: overflow-y-auto also clips horizontally, so the
                  scroll box needs interior room for the 4px focus ring; the negative
                  margin keeps the fields aligned with the dialog padding. */}
              <SubmittalForm
                lookups={lookups}
                members={members}
                companySuggestions={companySuggestions}
                showDisplayNumber
                displayNumberOptional
              />

              <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                  {t('Common_cancel')}
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                  {isSubmitting ? t('Common_saving') : t('Common_create')}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default SubmittalCreateDialog
